"use client";

import React, { useState } from 'react';
import Link from 'next/link';

const FAQConcernsSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "This sounds expensive. Do we have the budget?",
      answer: "Our assessment starts small (weeks, not months). You see exact scope and investment before committing to full implementation. We also offer phased approaches that spread investment over fiscal years."
    },
    {
      question: "We already use Power BI / Tableau. Why do we need EDW?",
      answer: "Dashboards are only as good as the data behind them. EDW provides the clean, unified, governed data layer that makes BI tools actually reliable. Without it, you're visualizing chaos."
    },
    {
      question: "Our data isn\'t clean yet. Should we fix that first?",
      answer: "EDW *is* how you clean data. Trying to clean it manually first creates rework. Our process includes data profiling, cleansing, and governance as core components."
    },
    {
      question: "Can our IT team handle this internally?",
      answer: "Your IT knows your systems; we bring specialized EDW expertise. Together, we deliver faster and avoid costly architectural mistakes. Many clients use our embedded model to upskill their teams."
    },
    {
      question: "We\'re too small for an enterprise data warehouse.",
      answer: "Size isn't the factor–data complexity is. If you have multiple systems, locations, or departments creating their own reports, EDW pays for itself quickly through time savings alone."
    },
    {
      question: "What's the ROI? How do we justify this to the board?",
      answer: "Our assessment includes ROI analysis: time savings, reduced errors, faster decisions, revenue opportunities. Most clients see payback in 12-18 months. We help you build the business case."
    },
    {
      question: "Is our data safe? What about compliance?",
      answer: "Absolutely. We implement role-based access, encryption, audit trails, and compliance frameworks (GDPR, HIPAA, SOC 2). You maintain 100% data ownership. Everything is NDA-backed."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="bg-white py-12 sm:py-16 md:py-24 min-h-0 flex items-center">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        {/* Headline */}
        <h2 className="font-serif text-navy text-[24px] sm:text-[32px] md:text-[40px] leading-[1.3] font-bold text-left md:text-center mb-4 sm:mb-6">
          Questions CFOs, CIOs & COOs Ask Us
        </h2>

        {/* Subheadline */}
        <p className="text-charcoal text-[15px] sm:text-base md:text-lg leading-[1.7] text-left md:text-center max-w-3xl md:mx-auto mb-10 sm:mb-16">
          We&apos;ve heard every objection. Here&apos;s how we address them.
        </p>

        {/* FAQ Accordion */}
        <div className="space-y-3 sm:space-y-4 mb-10 sm:mb-16">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-gray-300 rounded-lg overflow-hidden hover:border-gold transition-all"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-4 sm:py-5 text-left bg-white hover:bg-gray-50 active:bg-gray-100 transition-all min-h-[56px] touch-manipulation"
              >
                <span className="text-navy text-[15px] sm:text-lg font-bold pr-2 sm:pr-4 text-left">{faq.question}</span>
                <span className="text-gold text-xl sm:text-2xl flex-shrink-0 w-8 h-8 flex items-center justify-center">
                  {openIndex === index ? "−" : "+"}
                </span>
              </button>
              {openIndex === index && (
                <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gray-50 border-t border-gray-200">
                  <p className="text-charcoal text-[14px] sm:text-base leading-[1.7]">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex justify-start md:justify-center">
          <Link href="/contact" className="inline-block text-center w-full sm:w-auto min-h-[48px] bg-navy text-white px-6 sm:px-8 py-4 rounded-lg font-semibold text-[14px] sm:text-base hover:bg-navy/90 transition-all active:scale-[0.98]">
            Still have questions? Let&apos;s talk. →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FAQConcernsSection;